import type { MouseEvent } from 'react';

import { Button } from '@bookora/ui';
import type { ButtonProps } from '@bookora/ui';
import { useHref, useLinkClickHandler } from 'react-router-dom';

export type LinkButtonProps = Omit<ButtonProps, 'href' | 'onClick'> & { to: string };

/**
 * A button that navigates — as ONE element.
 *
 * `<Link><Button/></Link>` is an interactive element inside another: two tab
 * stops, the outer one nameless, and a doubled focus ring. AntD renders a
 * `Button` with `href` as a single `<a class="ant-btn">`, and the router's own
 * click handler keeps it a client-side navigation while leaving middle-click
 * and ⌘-click (new tab) to the browser.
 *
 * It IS the kit Button, so everything the kit Button explains itself with
 * comes along: `hint`, `shortcut`, and `disabledReason` (a blocked LinkButton
 * drops its `href` and does not navigate). It carries its own tooltip — never
 * wrap it in `<Tooltip>` (the `bookora/tooltip-wrap` lint guard).
 */
export function LinkButton({ to, ...rest }: LinkButtonProps) {
  const href = useHref(to);
  const handleClick = useLinkClickHandler<HTMLAnchorElement>(to);
  return (
    <Button
      {...rest}
      href={href}
      onClick={(event: MouseEvent<HTMLElement>) =>
        handleClick(event as MouseEvent<HTMLAnchorElement>)
      }
    />
  );
}
